import { cn } from "@/core/utils";
import type { CardListBlock, NoticeTone } from "../schemas/blocks";

/**
 * Renderer do block `card_list`: um cartão por item (ex.: chamados em aberto).
 * Cada card tem título, badge opcional (ex.: prioridade colorida) e uma lista de
 * campos rótulo/valor. O front só traduz o tone do badge em cor.
 */

function badgeClass(tone: NoticeTone): string {
  switch (tone) {
    case "success":
      return "bg-success-50 text-success-700 border-success-200";
    case "warning":
      return "bg-warning-50 text-warning-700 border-warning-200";
    case "danger":
      return "bg-danger-50 text-danger-700 border-danger-200";
    case "info":
      return "bg-primary-50 text-primary-700 border-primary-200";
    default:
      return "bg-lightgray-200 text-darkgray-700 border-lightgray-300";
  }
}

export function CardList({ block }: { block: CardListBlock }) {
  if (!block.items.length) return null;
  return (
    // Container query: responde à largura do CONTAINER do chat (não da tela). No chat
    // flutuante estreito fica 1 coluna; num painel/expandido largo, 2 colunas.
    <div className="@container">
      <div className="grid grid-cols-1 gap-2 @[520px]:grid-cols-2">
        {block.items.map((item, index) => (
          <div
            key={`${item.title}-${index}`}
            className="rounded-xl border border-lightgray-300 bg-white p-4"
          >
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-col">
                <span className="text-sm font-semibold text-darkgray-900">
                  {item.title}
                </span>
                {item.subtitle && (
                  <span className="text-xs text-darkgray-500">
                    {item.subtitle}
                  </span>
                )}
              </div>
              {item.badge && (
                <span
                  className={cn(
                    "shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium",
                    badgeClass(item.badge.tone),
                  )}
                >
                  {item.badge.text}
                </span>
              )}
            </div>
            {item.fields.length > 0 && (
              <dl className="flex flex-col gap-1">
                {item.fields.map((f, i) => (
                  <div key={`${f.label}-${i}`} className="flex gap-2 text-xs">
                    <dt className="shrink-0 text-darkgray-500">{f.label}:</dt>
                    <dd className="min-w-0 break-words text-darkgray-800">
                      {f.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
